import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Container, Typography, TextField, Button, Paper, Stack, Box,
  Snackbar, Alert, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
  Autocomplete, useMediaQuery, MenuItem, TablePagination,
  Avatar, Divider, InputAdornment, Menu, ListItemIcon, ListItemText
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  GridOn as GridOnIcon,
  CheckCircleOutline as CloseIcon,
  PlayCircleOutline as OpenIcon,
  Send as SendIcon,
  Save as SaveIcon,
  MoreVert as MoreVertIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import axios from "axios";
import * as XLSX from 'xlsx';

const API_BASE = `${process.env.REACT_APP_API_URL}/api/dpt/branch`;

// ===== WHITE THEME WITH YELLOW ACCENTS =====
const yellowButton = {
  background: "#FFD600",
  color: "#000000",
  fontWeight: "bold",
  "&:hover": {
    background: "#ffcc00",
    boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
  },
};

const lightStyles = {
  container: { minHeight: "100vh", bgcolor: "#f5f5f5", py: 2 },
  paper: {
    bgcolor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    color: "#000000",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  },
  dialogPaper: {
    bgcolor: "#ffffff",
    border: "2px solid #FFD600",
    color: "#000000",
    minWidth: { xs: "90%", sm: "700px" },
    borderRadius: "16px",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      color: "#000000",
      backgroundColor: "#ffffff",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#FFD600", borderWidth: "1.5px" },
      "&.Mui-focused fieldset": { borderColor: "#FFD600", borderWidth: "2px" },
    },
    "& .MuiInputLabel-root": { color: "#666666", fontSize: "0.8rem" },
    "& .MuiInputBase-input": { color: "#000000", fontSize: "0.85rem" },
    "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": { color: "#FFD600 !important" },
    "& .MuiSelect-icon": { color: "#FFD600" },
    "& select option": { bgcolor: "#ffffff", color: "#000000" }
  },
  actionBtn: {
    background: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    borderRadius: "10px",
    px: 3,
    "&:hover": {
      background: "#ffcc00",
      boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
    }
  },
  tableHeader: {
    color: "#000000",
    fontWeight: "bold",
    borderBottom: "2px solid #FFD600",
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    backgroundColor: "#f5f5f5",
    whiteSpace: 'nowrap',
    padding: '10px 14px',
  },
  tableCell: {
    color: "#000000",
    borderBottom: "1px solid #e0e0e0",
    whiteSpace: 'nowrap',
    padding: '10px 14px',
    fontSize: '0.8rem',
  }
};

const TicketCreation = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const token = localStorage.getItem("token");
  const messagesEndRef = useRef(null);
  const editInputRef = useRef(null);

  let user = null;
  let company = "";
  let currentUsername = "";
  let currentRole = "telecom";

  try {
    if (token) {
      user = JSON.parse(atob(token.split(".")[1]));
      company = user?.company || "Company";
      currentUsername = user?.username || user?.name || user?.company || "Telecom";
      currentRole = user?.role || "telecom";
    }
  } catch (err) {
    console.error("Invalid token format", err);
  }

  const [tickets, setTickets] = useState([]);
  const [branches, setBranches] = useState([]);
  const [newTicket, setNewTicket] = useState({
    serviceNumber: "",
    branchName: "",
    status: "Down",
    assignedTo: "ETC",
    CCTNumber: "",
    ticketStatus: "Open",
    comment: ""
  });
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [ticketSearch, setTicketSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Open");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Comment-related states
  const [commentsArray, setCommentsArray] = useState([]);
  const [conversationInput, setConversationInput] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editMessageText, setEditMessageText] = useState("");
  const [commentMenuAnchor, setCommentMenuAnchor] = useState(null);
  const [selectedCommentId, setSelectedCommentId] = useState(null);

  const showSnackbar = (msg, severity = "success") => setSnackbar({ open: true, message: msg, severity });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Check if comment belongs to current user (COMPANY_ADMIN)
  const isCurrentUserComment = useCallback((comment) => {
    return comment.role === "COMPANY_ADMIN";
  }, []);

  // ===== COMMENT PARSING FUNCTIONS =====
  const parseCommentsToArray = useCallback((comment) => {
    if (!comment) return [];
    const commentBlocks = [];
    const lines = comment.split("\n");
    let currentComment = null;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.startsWith("#")) {
        if (currentComment) commentBlocks.push(currentComment);
        currentComment = line;
      } else if (currentComment) {
        currentComment += "\n" + line;
      } else if (line.trim()) {
        commentBlocks.push(line);
      }
    }
    if (currentComment) commentBlocks.push(currentComment);

    const parsedComments = [];
    let counter = 0;
    for (let block of commentBlocks) {
      const firstLine = block.split("\n")[0];
      const match = firstLine.match(/^#(.*?)\[(.*?)\] (.*?): (.*)$/);
      if (match) {
        const lines = block.split("\n");
        const remainingLines = lines.slice(1);
        const fullMessage = match[4] + (remainingLines.length > 0 ? "\n" + remainingLines.join("\n") : "");
        parsedComments.push({
          id: `comment-${Date.now()}-${counter++}`,
          username: match[1],
          role: match[2],
          timestamp: match[3],
          message: fullMessage,
          raw: block,
          isEditing: false,
        });
      } else if (block.trim()) {
        parsedComments.push({
          id: `comment-${Date.now()}-${counter++}`,
          username: currentUsername,
          role: currentRole,
          timestamp: new Date().toLocaleString(),
          message: block,
          raw: block,
          isEditing: false,
        });
      }
    }
    return parsedComments;
  }, [currentUsername, currentRole]);

  const formatCommentsToString = useCallback((commentsArray) => {
    return commentsArray.map((comment) => comment.raw).join("\n");
  }, []);

  const addNewComment = useCallback((commentsArray, newMessage, username, role) => {
    const timestamp = new Date().toLocaleString();
    const rawComment = `#${username}[${role}] ${timestamp}: ${newMessage}`;
    const newComment = {
      id: `comment-${Date.now()}-${commentsArray.length}`,
      username,
      role,
      timestamp,
      message: newMessage,
      raw: rawComment,
      isEditing: false,
    };
    return [...commentsArray, newComment];
  }, []);

  const updateComment = useCallback((commentsArray, commentId, newMessage, username, role) => {
    const timestamp = new Date().toLocaleString();
    const oldComment = commentsArray.find((c) => c.id === commentId);
    const isEdited = oldComment && oldComment.message !== newMessage;
    const updatedRaw = `#${username}[${role}] ${timestamp}: ${newMessage}${isEdited ? " (edited)" : ""}`;
    return commentsArray.map((comment) =>
      comment.id === commentId
        ? { ...comment, message: newMessage, raw: updatedRaw, timestamp, isEditing: false }
        : comment
    );
  }, []);

  const deleteComment = useCallback((commentsArray, commentId) => {
    return commentsArray.filter((comment) => comment.id !== commentId);
  }, []);

  const getAvatarColor = useCallback((username) => {
    const colors = ["#FFD600", "#2196F3", "#9C27B0", "#4CAF50", "#FF9800", "#E91E63", "#00BCD4", "#795548"];
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
      hash = ((hash << 5) - hash) + username.charCodeAt(i);
      hash |= 0;
    }
    return colors[Math.abs(hash) % colors.length];
  }, []);

  // ===== COMMENT HANDLERS =====
  const handleSaveEdit = useCallback((commentId) => {
    if (!editMessageText.trim()) {
      showSnackbar("Message cannot be empty", "warning");
      return;
    }
    if (editMessageText.includes("#")) {
      showSnackbar("The '#' character is reserved for comment separation", "warning");
      return;
    }
    const comment = commentsArray.find((c) => c.id === commentId);
    if (comment) {
      const updatedComments = updateComment(commentsArray, commentId, editMessageText, comment.username, comment.role);
      const updatedCommentString = formatCommentsToString(updatedComments);
      setCommentsArray(updatedComments);
      setNewTicket((prev) => ({ ...prev, comment: updatedCommentString }));
      setEditingCommentId(null);
      setEditMessageText("");
      showSnackbar("Comment edited successfully", "success");
    }
  }, [editMessageText, commentsArray, updateComment, formatCommentsToString, showSnackbar]);

  const handleEditKeyPress = useCallback((e, commentId) => {
    if (e.key === "#") {
      e.preventDefault();
      showSnackbar("The '#' character is reserved for comment separation", "warning");
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit(commentId);
    }
  }, [handleSaveEdit, showSnackbar]);

  const handleSendMessage = useCallback(() => {
    if (!conversationInput.trim()) {
      showSnackbar("Please enter a message", "warning");
      return;
    }
    if (conversationInput.includes("#")) {
      showSnackbar("The '#' character is reserved for comment separation", "warning");
      return;
    }
    const updatedComments = addNewComment(commentsArray, conversationInput, currentUsername, currentRole);
    const updatedCommentString = formatCommentsToString(updatedComments);
    setCommentsArray(updatedComments);
    setNewTicket((prev) => ({ ...prev, comment: updatedCommentString }));
    setConversationInput("");
    showSnackbar("Message sent successfully", "success");
  }, [conversationInput, commentsArray, currentUsername, currentRole, addNewComment, formatCommentsToString, showSnackbar]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === "#") {
      e.preventDefault();
      showSnackbar("The '#' character is reserved for comment separation", "warning");
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage, showSnackbar]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    if (value.includes("#") && !conversationInput.includes("#")) {
      showSnackbar("The '#' character is reserved for comment separation", "warning");
      return;
    }
    setConversationInput(value);
  }, [conversationInput, showSnackbar]);

  const handleEditInputChange = useCallback((e) => {
    const value = e.target.value;
    if (value.includes("#") && !editMessageText.includes("#")) {
      showSnackbar("The '#' character is reserved for comment separation", "warning");
      return;
    }
    setEditMessageText(value);
  }, [editMessageText, showSnackbar]);

  const startEditing = useCallback((comment) => {
    if (isCurrentUserComment(comment)) {
      setEditingCommentId(comment.id);
      setEditMessageText(comment.message);
      handleCommentMenuClose();
    } else {
      showSnackbar("You can only edit your own comments", "warning");
    }
  }, [isCurrentUserComment, showSnackbar]);

  const cancelEditing = useCallback(() => {
    setEditingCommentId(null);
    setEditMessageText("");
  }, []);

  const handleCommentMenuOpen = useCallback((event, commentId) => {
    event.stopPropagation();
    setSelectedCommentId(commentId);
    setCommentMenuAnchor(event.currentTarget);
  }, []);

  const handleCommentMenuClose = useCallback(() => {
    setCommentMenuAnchor(null);
    setSelectedCommentId(null);
  }, []);

  const handleDeleteComment = useCallback(() => {
    const commentToDelete = commentsArray.find(c => c.id === selectedCommentId);
    if (!commentToDelete) return;
    if (!isCurrentUserComment(commentToDelete)) {
      showSnackbar("You can only delete your own comments", "warning");
      handleCommentMenuClose();
      return;
    }
    if (selectedCommentId && window.confirm("Are you sure you want to delete this comment?")) {
      const updatedComments = deleteComment(commentsArray, selectedCommentId);
      const updatedCommentString = formatCommentsToString(updatedComments);
      setCommentsArray(updatedComments);
      setNewTicket((prev) => ({ ...prev, comment: updatedCommentString }));
      showSnackbar("Comment deleted successfully", "success");
    }
    handleCommentMenuClose();
  }, [selectedCommentId, commentsArray, deleteComment, formatCommentsToString, showSnackbar, handleCommentMenuClose, isCurrentUserComment]);

  // Load comments when ticket comment changes
  useEffect(() => {
    if (newTicket.comment) {
      setCommentsArray(parseCommentsToArray(newTicket.comment));
    } else {
      setCommentsArray([]);
    }
  }, [newTicket.comment, parseCommentsToArray]);

  useEffect(() => {
    if (commentsArray.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [commentsArray, scrollToBottom]);

  useEffect(() => {
    if (editingCommentId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingCommentId]);

  // ===== FETCH FUNCTIONS =====
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [ticketRes, branchRes] = await Promise.all([
        axios.get(`${API_BASE}/tickets`, { headers }),
        axios.get(`${API_BASE}/all`, { headers })
      ]);
      const ticketsData = ticketRes.data?.data || [];
      setTickets(ticketsData);
      const branchesData = Array.isArray(branchRes.data) ? branchRes.data : (branchRes.data?.data || []);
      setBranches(branchesData);
    } catch (err) {
      console.error("Fetch error:", err);
      showSnackbar("Terminal Sync Failure", "error");
      setTickets([]);
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleDeleteTicket = async (id) => {
    if (!window.confirm("Confirm permanent deletion of this record?")) return;
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_BASE}/ticket/${id}`, config);
      showSnackbar("Record purged from system");
      fetchTickets();
    } catch (err) {
      showSnackbar("Purge failed: Access Denied", "error");
    }
  };

  const handleExportExcel = () => {
    const exportData = filteredTickets.map(ticket => ({
      'Service Number': ticket.serviceNumber,
      'Branch Name': ticket.name,
      'Region': ticket.region || 'N/A',
      'District': ticket.district || 'N/A',
      'Status': ticket.status,
      'Assignee': ticket.assignedTo,
      'CCT Number': ticket.CCTNumber,
      'Ticket Status': ticket.ticketStatus,
      'Comment': ticket.comment || 'N/A',
      'Opened At': formatDateTime(ticket.openedAt),
      'Closed At': formatDateTime(ticket.closedAt),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tickets");
    XLSX.writeFile(wb, `Ticket_Registry_${new Date().toISOString().split('T')[0]}.xlsx`);
    showSnackbar("Excel Exported Successfully");
  };

  // ===== TICKET HANDLERS =====
  const saveTicket = async () => {
    if (!newTicket.serviceNumber) return showSnackbar("Selection Required", "error");

    let finalCommentString = newTicket.comment;
    if (conversationInput.trim()) {
      if (conversationInput.includes("#")) {
        showSnackbar("The '#' character is reserved for comment separation", "warning");
        return;
      }
      const updatedComments = addNewComment(commentsArray, conversationInput, currentUsername, currentRole);
      finalCommentString = formatCommentsToString(updatedComments);
    }

    const payload = { ...newTicket, comment: finalCommentString || "" };

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (editId) {
        await axios.put(`${API_BASE}/ticket/${editId}`, payload, config);
        showSnackbar("Ticket Updated Successfully");
      } else {
        await axios.post(`${API_BASE}/create/ticket`, payload, config);
        showSnackbar("Ticket Created Successfully");
      }
      setIsPopupOpen(false);
      setEditId(null);
      setConversationInput("");
      setCommentsArray([]);
      setNewTicket({
        serviceNumber: "",
        branchName: "",
        status: "Down",
        assignedTo: "ETC",
        CCTNumber: "",
        ticketStatus: "Open",
        comment: ""
      });
      fetchTickets();
    } catch (err) {
      const message = err.response?.data?.msg || err.response?.data?.message || err.message || "Protocol Error";
      showSnackbar(message, "error");
    }
  };

  const handleOpenEdit = useCallback((ticket) => {
    setEditId(ticket._id);
    setNewTicket({
      serviceNumber: ticket.serviceNumber,
      branchName: ticket.name,
      status: ticket.status,
      assignedTo: ticket.assignedTo,
      ticketStatus: ticket.ticketStatus,
      CCTNumber: ticket.CCTNumber || "",
      comment: ticket.comment || ""
    });
    setConversationInput("");
    setEditingCommentId(null);
    setIsPopupOpen(true);
  }, []);

  // Function to handle opening create ticket dialog
  const handleOpenCreate = useCallback(() => {
    setEditId(null);
    setNewTicket({
      serviceNumber: "",
      branchName: "",
      status: "Down",
      assignedTo: "ETC",
      CCTNumber: "Pending",
      ticketStatus: "Open",
      comment: ""
    });
    setConversationInput("");
    setCommentsArray([]);
    setEditingCommentId(null);
    setIsPopupOpen(true);
  }, []);

  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      const searchableText = `${t.serviceNumber} ${t.name} ${t.CCTNumber} ${t.region} ${t.district} ${t.comment || ''}`.toLowerCase();
      const matchesSearch = searchableText.includes(ticketSearch.toLowerCase());
      const matchesStatus = statusFilter === "All" ? true : t.ticketStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [tickets, ticketSearch, statusFilter]);

  const paginatedTickets = useMemo(() => {
    return filteredTickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredTickets, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isTicketToggleDisabled = (ticket) => {
    return ticket.status === "Active" && ticket.ticketStatus === "Closed";
  };

  const handleToggleState = async (ticket) => {
    if (isTicketToggleDisabled(ticket)) {
      showSnackbar("Cannot reopen ticket for active branch", "warning");
      return;
    }
    const isClosing = ticket.ticketStatus === "Open";
    const now = new Date().toISOString();
    const updatedTicket = {
      ...ticket,
      ticketStatus: isClosing ? "Closed" : "Open",
      status: isClosing ? "Active" : ticket.status,
      closedAt: isClosing ? now : null,
      openedAt: isClosing ? ticket.openedAt : now
    };
    try {
      await axios.put(`${API_BASE}/ticket/${ticket._id}`, updatedTicket, { headers: { Authorization: `Bearer ${token}` } });
      showSnackbar(`Ticket ${isClosing ? "Closed" : "Opened"} Successfully`);
      fetchTickets();
    } catch (err) {
      showSnackbar("Toggle failed", "error");
    }
  };

  useEffect(() => {
    setPage(0);
  }, [ticketSearch, statusFilter]);

  const formatDateTime = (date) => {
    if (!date) return "Pending";
    return new Date(date).toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  function getDurationInmins(from, to) {
    if (!from || !to) return 0;
    const diffMs = new Date(to) - new Date(from);
    return Math.floor(diffMs / 60000);
  }

  function formatDuration(totalMinutes) {
    const days = Math.floor(totalMinutes / (24 * 60));
    totalMinutes %= 24 * 60;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    let result = "";
    if (days > 0) result += `${days}d `;
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}m`;
    return result.trim() || "0m";
  }

  const getTableHeight = () => {
    const toolbarHeight = 80;
    const paginationHeight = 80;
    const headerHeight = 100;
    const availableHeight = window.innerHeight - (toolbarHeight + paginationHeight + headerHeight);
    return `${Math.max(availableHeight, 300)}px`;
  };

  const getProblematicBranches = () => {
    const problematicStatuses = ["Down", "Intermittent", "NTK_Down", "All_Down", "SYM_D&Int"];
    return branches.filter(branch =>
      problematicStatuses.some(status => branch.status?.includes(status))
    );
  };

  const getStatusColor = (status) => {
    if (status?.includes("Down") || status?.includes("NTK_Down") || status?.includes("All_Down")) {
      return "#ff4444";
    }
    if (status?.includes("Int") || status?.includes("SYM_D&Int")) {
      return "#ff9800";
    }
    return "#00e676";
  };

  const normalizeStatus = (branchStatus) => {
    if (branchStatus?.includes("Down") || branchStatus?.includes("NTK_Down") || branchStatus?.includes("All_Down")) {
      return "Down";
    }
    if (branchStatus?.includes("Int") || branchStatus?.includes("SYM_D&Int")) {
      return "Intermittent";
    }
    return "Active";
  };

  const getLastComment = (comment) => {
    const comments = parseCommentsToArray(comment);
    return comments[comments.length - 1]?.message || "No comments";
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: 2 }}>
      <Container
              maxWidth={false}
              sx={{
                width: { xs: "100%", md: "calc(100% - 96px)" },
                ml: { xs: 0, md: "84px" },
                mr: { xs: 0, md: "12px" },
                px: { xs: 1, md: 0.5 },
              }}
            >
        <Paper sx={{ ...lightStyles.paper, overflow: 'hidden' }}>
          {/* Header Section */}
<Box sx={{ 
  p: 2, 
  borderBottom: '1px solid #e0e0e0', 
  backgroundColor: '#fafafa',
  // Desktop: flex container for single row
  '@media (min-width: 768px)': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  }
}}>
  {/* Left side: New Ticket, Search, Count */}
  <Stack 
    direction="row" 
    spacing={1} 
    alignItems="center" 
    sx={{ 
      mb: 1.5,
      flex: 1,
      // Desktop: remove margin and allow shrink
      '@media (min-width: 768px)': {
        mb: 0,
        flex: '0 1 auto',
      }
    }}
  >
    {/* Mobile: Icon button */}
    <IconButton 
      onClick={handleOpenCreate} 
      sx={{ 
        ...lightStyles.actionBtn, 
        borderRadius: '4px', 
        width: 40, 
        height: 40,
        flexShrink: 0,
        '@media (min-width: 768px)': {
          display: 'none',
        }
      }}
    >
      <AddIcon />
    </IconButton>
    
    {/* Desktop: Full button */}
    <Button 
      variant="contained" 
      startIcon={<AddIcon />} 
      onClick={handleOpenCreate} 
      sx={{ 
        ...lightStyles.actionBtn, 
        whiteSpace: 'nowrap',
        display: 'none',
        '@media (min-width: 768px)': {
          display: 'inline-flex',
        }
      }}
    >
      New Ticket
    </Button>
    
    <TextField
      placeholder="Search..."
      size="small"
      sx={{ 
        ...lightStyles.input, 
        flexGrow: 1,
        '& .MuiInputBase-root': {
          height: 40
        },
        // Desktop: fixed width
        '@media (min-width: 768px)': {
          flexGrow: 0,
          width: 250,
        }
      }}
      value={ticketSearch}
      onChange={(e) => setTicketSearch(e.target.value)}
    />
    
    <Typography 
      variant="caption" 
      sx={{ 
        color: "#000000", 
        fontWeight: "bold", 
        border: "1px solid #e0e0e0", 
        px: 1.5, 
        py: 0.75, 
        borderRadius: "4px", 
        bgcolor: "rgba(255,214,0,0.05)", 
        whiteSpace: "nowrap",
        minWidth: 30,
        textAlign: 'center',
        flexShrink: 0
      }}
    >
      {filteredTickets.length}
    </Typography>
  </Stack>

  {/* Right side: Export, Filter, Refresh */}
  <Stack 
    direction="row" 
    spacing={1} 
    alignItems="center"
    sx={{
      // Desktop: prevent shrinking
      '@media (min-width: 768px)': {
        flex: '0 0 auto',
      }
    }}
  >
    <Tooltip title="Export Excel">
      <IconButton
        onClick={handleExportExcel}
        sx={{
          ...lightStyles.actionBtn,
          padding: '6px',
          width: '40px',
          height: '40px',
          flexShrink: 0,
          '& .MuiSvgIcon-root': {
            fontSize: '24px',
          }
        }}
      >
        <GridOnIcon />
      </IconButton>
    </Tooltip>
    
    <TextField
      select
      size="small"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      label="Filter State"
      sx={{ 
        ...lightStyles.input,
        '& .MuiInputBase-root': {
          height: 40
        },
        // Mobile: flexible, Desktop: fixed width
        flexGrow: 1,
        '@media (min-width: 768px)': {
          flexGrow: 0,
          width: 160,
        }
      }}
    >
      <MenuItem value="All">All Status</MenuItem>
      <MenuItem value="Open">Open</MenuItem>
      <MenuItem value="Closed">Closed</MenuItem>
    </TextField>
    
    <IconButton 
      onClick={fetchTickets} 
      sx={{ 
        ...yellowButton, 
        background: "transparent", 
        border: "1px solid #FFD600", 
        flexShrink: 0,
        width: 40,
        height: 40
      }}
    >
      <RefreshIcon />
    </IconButton>
  </Stack>
</Box>

          {/* Table Section */}
          <TableContainer sx={{ height: getTableHeight(), maxHeight: getTableHeight(), overflowX: "auto", overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: "8px", "&::-webkit-scrollbar": { width: "8px", height: "8px" }, "&::-webkit-scrollbar-track": { bgcolor: "#f5f5f5" }, "&::-webkit-scrollbar-thumb": { bgcolor: "#FFD600", borderRadius: "4px" } }}>
                      {loading ? (
                        <Box p={4} textAlign="center">
                          <CircularProgress sx={{ color: "#FFD600" }} />
                          <Typography sx={{ mt: 2, color: "#666" }}>Loading tickets...</Typography>
                        </Box>
                      ) : (
                        <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={lightStyles.tableHeader}>Branch</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Service #</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Wan IP</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Primary.no</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Secondary.no</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Status</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Ticket</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>CCT #</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Duration</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Assignee</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Actions</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>TechPrimary.no</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>TechSecondary.no</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Created By</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Opened At</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Closed At</TableCell>
                              <TableCell sx={lightStyles.tableHeader}>Last Comment</TableCell>
                              
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedTickets.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4, color: "#666" }}>No tickets found</TableCell>
                              </TableRow>
                            ) : (
                              paginatedTickets.map((t) => {
                                const disabled = isTicketToggleDisabled(t);
                                return (
                                  <TableRow key={t._id} sx={{ "&:hover": { bgcolor: "rgba(255, 214, 0, 0.05)" } }}>
                                    <TableCell sx={lightStyles.tableCell}>{t.name}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{t.serviceNumber}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{t.wanIp}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{t.primaryContact}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{t.secondaryContact}</TableCell>
                                    <TableCell sx={{ ...lightStyles.tableCell, fontWeight: 'bold', color: getStatusColor(t.status), whiteSpace: 'nowrap' }}>{t.status}</TableCell>
                                    <TableCell sx={{ ...lightStyles.tableCell, fontWeight: 'bold', color: t.ticketStatus === "Open" ? "#ff0000" : "#00e676", whiteSpace: 'nowrap' }}>{t.ticketStatus}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{t.CCTNumber || 'Pending'}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{formatDuration(getDurationInmins(t.openedAt, t.closedAt))}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{t.assignedTo}</TableCell>
                                    <TableCell sx={{ ...lightStyles.tableCell, whiteSpace: 'nowrap' }}>
                                      <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Tooltip title="View/Edit Ticket">
                                          <IconButton size="small" onClick={() => handleOpenEdit(t)} sx={{ color: "#000000", border: "1px solid #e0e0e0", "&:hover": { bgcolor: "#FFD600", borderColor: "#FFD600" } }}>
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={disabled ? "Cannot reopen: Branch is Active" : (t.ticketStatus === "Open" ? "Close Ticket" : "Reopen Ticket")}>
                                          <IconButton size="small" disabled={disabled} onClick={() => handleToggleState(t)} sx={{ color: t.ticketStatus === "Open" ? "#ff9800" : "#00e676", opacity: disabled ? 0.4 : 1, border: "1px solid #e0e0e0", "&:hover": { bgcolor: disabled ? "transparent" : "#FFD600" } }}>
                                            {t.ticketStatus === "Open" ? <CloseIcon fontSize="small" /> : <OpenIcon fontSize="small" />}
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Ticket">
                                          <IconButton size="small" sx={{ color: "#ff4444", border: "1px solid #e0e0e0", "&:hover": { bgcolor: "#ff0000", color: "#fff" } }} onClick={() => handleDeleteTicket(t._id)}>
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    </TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{t.techprimaryContact}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{t.techsecondaryContact}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{t.createdBy}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>{formatDateTime(t.openedAt)}</TableCell>
                                    <TableCell sx={lightStyles.tableCell}>
                                      {t.ticketStatus === "Open" ? "Pending" : formatDateTime(t.closedAt)}
                                    </TableCell>
                                    <TableCell sx={{ ...lightStyles.tableCell, maxWidth: 250, whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                      <Tooltip title={getLastComment(t.comment)}>
                                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                          {getLastComment(t.comment)}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      )}
                    </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredTickets.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ color: "#000000", borderTop: "1px solid #e0e0e0", "& .MuiTablePagination-selectIcon": { color: "#FFD600" }, "& .MuiIconButton-root": { color: "#FFD600" }, whiteSpace: 'nowrap' }}
          />
        </Paper>

        {/* Comment Action Menu */}
        <Menu anchorEl={commentMenuAnchor} open={Boolean(commentMenuAnchor)} onClose={handleCommentMenuClose}>
          <MenuItem onClick={() => { const comment = commentsArray.find(c => c.id === selectedCommentId); if (comment) startEditing(comment); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit Comment</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteComment}>
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Delete Comment</ListItemText>
          </MenuItem>
        </Menu>

        {/* Dialog for Create/Edit Ticket with Comment System */}
        <Dialog open={isPopupOpen} onClose={() => { setIsPopupOpen(false); setEditId(null); setConversationInput(""); setEditingCommentId(null); }} PaperProps={{ sx: lightStyles.dialogPaper }} fullWidth maxWidth="md">
          <DialogTitle sx={{ color: "#FFD600", borderBottom: '1px solid #e0e0e0', fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {editId ? "EDIT TICKET" : "CREATE NEW TICKET"}
            <IconButton size="small" onClick={() => setIsPopupOpen(false)} sx={{ color: "#666" }}>✖</IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              {/* Branch Selection (only for new tickets) */}
              {!editId ? (
                <Autocomplete
                  fullWidth
                  options={getProblematicBranches()}
                  getOptionLabel={(o) => `${o.name} (${o.serviceNumber}) - ${o.status}`}
                  value={branches.find(b => b.serviceNumber === newTicket.serviceNumber) || null}
                  onChange={(_, val) => {
                    if (!val) {
                      setNewTicket(prev => ({ ...prev, serviceNumber: "", branchName: "", status: "Down", assignedTo: "ETC" }));
                      return;
                    }
                    const normalizedStatus = normalizeStatus(val.status);
                    const assignedTo = val.problem === "ETC" ? "ETC" : company;
                    setNewTicket(prev => ({ ...prev, serviceNumber: val.serviceNumber, branchName: val.name, status: normalizedStatus, assignedTo }));
                  }}
                  renderOption={(props, option) => (
                    <li {...props} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px" }}>
                      <Box>
                        <Typography sx={{ fontSize: "0.85rem", color: "#000" }}>{option.name}</Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: "#666" }}>SN: {option.serviceNumber}</Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: "bold", color: getStatusColor(option.status) }}>{option.status}</Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: "#999" }}>{option.problem}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Search Branch / Service Number" sx={lightStyles.input} />}
                  componentsProps={{ popper: { sx: lightStyles.autocompletePopper } }}
                />
              ) : (
                <Paper elevation={0} sx={{ p: 2, bgcolor: "#f5f5f5", borderRadius: "12px" }}>
                  <Typography variant="caption" color="textSecondary">Ticket Information (Read Only)</Typography>
                  <Stack spacing={1} mt={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="textSecondary">Branch:</Typography>
                      <Typography variant="body2" fontWeight={600}>{newTicket.branchName}</Typography>
                    </Box>
                    <Divider />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="textSecondary">Service Number:</Typography>
                      <Typography variant="body2" fontWeight={600}>{newTicket.serviceNumber}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}

              {/* Editable Fields */}
              <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={newTicket.status}
                  onChange={(e) => setNewTicket({ ...newTicket, status: e.target.value })}
                  sx={lightStyles.input}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Intermittent">Intermittent</MenuItem>
                  <MenuItem value="Down">Down</MenuItem>
                </TextField>

                <TextField
                  select
                  fullWidth
                  label="Assignee"
                  value={newTicket.assignedTo}
                  onChange={(e) => setNewTicket({ ...newTicket, assignedTo: e.target.value })}
                  sx={lightStyles.input}
                >
                  <MenuItem value="ETC">ETC</MenuItem>
                  <MenuItem value={company}>{company}</MenuItem>
                </TextField>

                <TextField
                  label="CCT Number"
                  name="CCTNumber"
                  value={editId ? (newTicket.CCTNumber || "") : "Pending"}
                  onChange={(e) => {
                    if (editId) {
                      setNewTicket({ ...newTicket, CCTNumber: e.target.value });
                    }
                  }}
                  InputProps={{
                    readOnly: !editId,
                  }}
                  sx={{
                    ...lightStyles.input,
                    ...(!editId && {
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f5f5f5",
                      },
                      "& .MuiInputBase-input": {
                        color: "#666666",
                      },
                    }),
                  }}
                  fullWidth
                />

                <TextField
                  select
                  fullWidth
                  label="Ticket State"
                  value={newTicket.ticketStatus}
                  onChange={(e) => setNewTicket({ ...newTicket, ticketStatus: e.target.value })}
                  sx={lightStyles.input}
                >
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </TextField>
              </Stack>

              {/* Comments Section - Aligned based on role */}
              {editId && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1, color: "#FFD600", fontWeight: "bold" }}>Conversation Thread</Typography>
                  <Paper sx={{ height: 300, overflow: "auto", p: 2, bgcolor: "#f9f9f9", borderRadius: "12px" }}>
                    {commentsArray.length > 0 ? (
                      <Stack spacing={1.5}>
                        {commentsArray.map((comment) => {
                          // Check if comment is from COMPANY_ADMIN
                          const isCompanyAdmin = comment.role === "COMPANY_ADMIN";
                          return (
                            <Box
                              key={comment.id}
                              sx={{
                                display: "flex",
                                justifyContent: isCompanyAdmin ? "flex-end" : "flex-start",
                              }}
                            >
                              <Box sx={{ maxWidth: "80%" }}>
                                {editingCommentId === comment.id ? (
                                  <Paper sx={{ p: 1.5, border: "2px solid #FFD600", borderRadius: "12px" }}>
                                    <TextField
                                      inputRef={editInputRef}
                                      multiline
                                      rows={3}
                                      fullWidth
                                      value={editMessageText}
                                      onChange={handleEditInputChange}
                                      onKeyPress={(e) => handleEditKeyPress(e, comment.id)}
                                      autoFocus
                                      sx={lightStyles.input}
                                    />
                                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                                      <Button size="small" onClick={cancelEditing} sx={{ color: "#666" }}>Cancel</Button>
                                      <Button size="small" onClick={() => handleSaveEdit(comment.id)} sx={yellowButton}>Save</Button>
                                    </Box>
                                  </Paper>
                                ) : (
                                  <>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 0.5,
                                        justifyContent: isCompanyAdmin ? "flex-end" : "flex-start",
                                      }}
                                    >
                                      <Avatar
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          fontSize: "0.75rem",
                                          bgcolor: getAvatarColor(comment.username),
                                          order: isCompanyAdmin ? 1 : 0,
                                        }}
                                      >
                                        {comment.username?.charAt(0).toUpperCase()}
                                      </Avatar>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: "bold",
                                          order: isCompanyAdmin ? 0 : 1,
                                        }}
                                      >
                                        {comment.username} ({comment.role})
                                      </Typography>
                                      {isCompanyAdmin && (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => handleCommentMenuOpen(e, comment.id)}
                                          sx={{ p: 0, order: 2 }}
                                        >
                                          <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                    <Paper
                                      sx={{
                                        p: 1.5,
                                        bgcolor: isCompanyAdmin ? "#FFD60020" : "#ffffff",
                                        borderRadius: isCompanyAdmin
                                          ? "16px 16px 4px 16px"
                                          : "16px 16px 16px 4px",
                                        border: "1px solid #e0e0e0",
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                        {comment.message}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "#666", display: "block", mt: 0.5 }}
                                      >
                                        {comment.timestamp}
                                      </Typography>
                                      {comment.message.includes("(edited)") && (
                                        <Typography
                                          variant="caption"
                                          sx={{ color: "#999", fontStyle: "italic", mt: 0.5, display: "block" }}
                                        >
                                          (edited)
                                        </Typography>
                                      )}
                                    </Paper>
                                  </>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </Stack>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <Typography variant="body2" sx={{ color: "#999" }}>No messages yet</Typography>
                      </Box>
                    )}
                  </Paper>

                  {/* Message Input */}
                  {newTicket.ticketStatus !== "Closed" && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <TextField
                        placeholder="Type your message here..."
                        multiline
                        rows={2}
                        fullWidth
                        value={conversationInput}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        sx={lightStyles.input}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={handleSendMessage} sx={{ bgcolor: "#FFD600", "&:hover": { bgcolor: "#ffcc00" } }}>
                                <SendIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
            <Button onClick={() => { setIsPopupOpen(false); setEditId(null); setConversationInput(""); setEditingCommentId(null); }} sx={{ color: "#666666" }}>Cancel</Button>
            <Button variant="contained" onClick={saveTicket} sx={lightStyles.actionBtn}>{editId ? "Update Ticket" : "Create Ticket"}</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity} sx={{ bgcolor: "#ffffff", color: "#000000", border: "1px solid #FFD600" }}>{snackbar.message}</Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default TicketCreation;